/**
 * Frontend destination types for activQMS platform
 *
 * This file contains TypeScript types specific to the frontend
 * destination functionality, extending the core types.
 */

import type {
  DestinationEntity,
  DestinationCategory,
  DestinationAnalytics,
  CreateDestinationInput,
  UpdateDestinationInput,
  DestinationFilterInput,
} from "./destination-core-types";

// Form state types
export interface DestinationFormData {
  name: string;
  url: string;
  category: DestinationCategory;
  description: string;
  tags: string[];
  trackingEnabled: boolean;
  file?: File;
  fileName?: string;
}

export interface DestinationFormErrors {
  name?: string;
  url?: string;
  category?: string;
  description?: string;
  tags?: string;
  file?: string;
  general?: string;
}

export interface DestinationFormState {
  data: DestinationFormData;
  errors: DestinationFormErrors;
  isValid: boolean;
  isSubmitting: boolean;
  isDirty: boolean;
}

// UI state types
export interface DestinationListState {
  destinations: DestinationEntity[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  selectedCategory: DestinationCategory | "ALL";
  sortBy: "name" | "created" | "clicks" | "conversion";
  sortOrder: "asc" | "desc";
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
}

export interface DestinationModalState {
  isOpen: boolean;
  mode: "create" | "edit" | "view" | "delete";
  destination: DestinationEntity | null;
}

// Analytics display types
export interface DestinationAnalyticsDisplay {
  totalClicks: number;
  uniqueClicks: number;
  conversionRate: number;
  lastClickAt: string | null;
  deviceBreakdown: {
    label: string;
    value: number;
    percentage: number;
    color: string;
  }[];
  locationBreakdown: {
    country: string;
    region?: string;
    city?: string;
    clicks: number;
    percentage: number;
  }[];
  hourlyBreakdown: {
    hour: number;
    clicks: number;
    label: string;
  }[];
  timeSeriesData: {
    date: string;
    clicks: number;
    uniqueClicks: number;
    conversionRate?: number;
  }[];
}

// Category configuration
export interface DestinationCategoryConfig {
  value: DestinationCategory;
  label: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
}

export const DESTINATION_CATEGORIES: DestinationCategoryConfig[] = [
  {
    value: "LANDING_PAGE",
    label: "Landing Page",
    description:
      "Dedicated pages designed for specific conversations and conversions",
    icon: "document",
    color: "blue",
    bgColor: "bg-blue-100",
    textColor: "text-blue-800",
  },
  {
    value: "PRODUCT_PAGE",
    label: "Product Page",
    description: "Product showcase and information pages",
    icon: "shopping-bag",
    color: "green",
    bgColor: "bg-green-100",
    textColor: "text-green-800",
  },
  {
    value: "CONTACT_FORM",
    label: "Contact Form",
    description: "Contact and inquiry forms for lead generation",
    icon: "mail",
    color: "purple",
    bgColor: "bg-purple-100",
    textColor: "text-purple-800",
  },
  {
    value: "DOWNLOAD_PAGE",
    label: "Download Page",
    description: "File downloads and resource pages",
    icon: "download",
    color: "orange",
    bgColor: "bg-orange-100",
    textColor: "text-orange-800",
  },
  {
    value: "EXTERNAL_SITE",
    label: "External Site",
    description: "External websites and partner pages",
    icon: "external-link",
    color: "gray",
    bgColor: "bg-gray-100",
    textColor: "text-gray-800",
  },
  {
    value: "CHAT_INTERFACE",
    label: "Chat Interface",
    description: "Direct users to chat interfaces for real-time engagement",
    icon: "chat",
    color: "indigo",
    bgColor: "bg-indigo-100",
    textColor: "text-indigo-800",
  },
  {
    value: "FILE",
    label: "File",
    description: "Upload files (PDFs, documents) for direct download",
    icon: "document-text",
    color: "red",
    bgColor: "bg-red-100",
    textColor: "text-red-800",
  },
];

// Validation rules
export interface DestinationValidationRules {
  name: {
    required: boolean;
    minLength: number;
    maxLength: number;
    pattern?: RegExp;
  };
  url: {
    required: boolean;
    maxLength: number;
    pattern: RegExp;
  };
  description: {
    maxLength: number;
  };
  tags: {
    maxCount: number;
    maxLength: number;
  };
}

export const DESTINATION_VALIDATION_RULES: DestinationValidationRules = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-_.,!?()]+$/,
  },
  url: {
    required: true,
    maxLength: 2048,
    pattern: /^https?:\/\/.+/,
  },
  description: {
    maxLength: 500,
  },
  tags: {
    maxCount: 10,
    maxLength: 50,
  },
};

// Table column configuration
export interface DestinationTableColumn {
  key: string;
  label: string;
  sortable: boolean;
  width?: string;
  align?: "left" | "center" | "right";
  render?: (destination: DestinationEntity) => React.ReactNode;
}

// Filter options
export interface DestinationFilterOptions {
  categories: Array<{
    value: DestinationCategory | "ALL";
    label: string;
    count?: number;
  }>;
  statuses: Array<{
    value: boolean | "ALL";
    label: string;
    count?: number;
  }>;
  tags: Array<{
    value: string;
    label: string;
    count?: number;
  }>;
}

// Search and filter state
export interface DestinationSearchState {
  query: string;
  category: DestinationCategory | "ALL";
  status: boolean | "ALL";
  tags: string[];
  dateRange: {
    from?: string;
    to?: string;
  };
}

// Bulk operations
export interface DestinationBulkOperation {
  type: "delete" | "activate" | "deactivate" | "attach" | "detach";
  destinationIds: string[];
  conversationId?: string;
}

export interface DestinationBulkOperationResult {
  success: boolean;
  processed: number;
  failed: number;
  errors: string[];
}

// Export/Import types
export interface DestinationExportOptions {
  format: "csv" | "json";
  includeAnalytics: boolean;
  dateRange?: {
    from: string;
    to: string;
  };
  filter?: DestinationFilterInput;
}

export interface DestinationImportData {
  name: string;
  url: string;
  category: string;
  description?: string;
  tags?: string;
  trackingEnabled?: boolean;
}

export interface DestinationImportResult {
  success: boolean;
  imported: number;
  failed: number;
  errors: Array<{
    row: number;
    field: string;
    message: string;
  }>;
}

// Component props types
export interface DestinationCardProps {
  destination: DestinationEntity;
  onEdit?: (destination: DestinationEntity) => void;
  onDelete?: (destination: DestinationEntity) => void;
  onView?: (destination: DestinationEntity) => void;
  showAnalytics?: boolean;
  compact?: boolean;
  deleting?: boolean;
}

export interface DestinationFormProps {
  destination?: DestinationEntity;
  onSubmit: (data: DestinationFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  mode: "create" | "edit";
}

export interface DestinationAnalyticsProps {
  destinationId: string;
  dateRange?: {
    from: string;
    to: string;
  };
  showDetails?: boolean;
  compact?: boolean;
}

// Notification types
export interface DestinationNotification {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

// Loading states
export interface DestinationLoadingStates {
  list: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  analytics: boolean;
  attach: boolean;
  detach: boolean;
}

// Error types
export interface DestinationErrorState {
  code?: string;
  message: string;
  field?: string;
  details?: Record<string, unknown>;
}

// Utility types
export type DestinationSortField =
  | "name"
  | "created"
  | "updated"
  | "clicks"
  | "conversion";
export type DestinationSortOrder = "asc" | "desc";

export interface DestinationSort {
  field: DestinationSortField;
  order: DestinationSortOrder;
}

// Default values
export const DEFAULT_DESTINATION_FORM_DATA: DestinationFormData = {
  name: "",
  url: "",
  category: "LANDING_PAGE",
  description: "",
  tags: [],
  trackingEnabled: true,
  file: undefined,
  fileName: "",
};

export const DEFAULT_DESTINATION_SEARCH_STATE: DestinationSearchState = {
  query: "",
  category: "ALL",
  status: "ALL",
  tags: [],
  dateRange: {},
};

// Helper functions
export function getCategoryConfig(
  category: DestinationCategory
): DestinationCategoryConfig {
  return (
    DESTINATION_CATEGORIES.find((c) => c.value === category) ||
    DESTINATION_CATEGORIES[0]
  );
}

export function formatDestinationUrl(url: string): string {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `https://${url}`;
  }
  return url;
}

export function validateDestinationForm(
  data: DestinationFormData
): DestinationFormErrors {
  const errors: DestinationFormErrors = {};

  // Name validation
  if (!data.name.trim()) {
    errors.name = "Name is required";
  } else if (data.name.length < DESTINATION_VALIDATION_RULES.name.minLength) {
    errors.name = `Name must be at least ${DESTINATION_VALIDATION_RULES.name.minLength} characters`;
  } else if (data.name.length > DESTINATION_VALIDATION_RULES.name.maxLength) {
    errors.name = `Name must be less than ${DESTINATION_VALIDATION_RULES.name.maxLength} characters`;
  } else if (
    DESTINATION_VALIDATION_RULES.name.pattern &&
    !DESTINATION_VALIDATION_RULES.name.pattern.test(data.name)
  ) {
    errors.name = "Name contains invalid characters";
  }

  // URL or File validation based on category
  if (data.category === "FILE") {
    // For FILE category, validate file instead of URL
    if (!data.file && !data.fileName) {
      errors.file = "File is required for FILE category";
    } else if (data.file) {
      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (data.file.size > maxSize) {
        errors.file = "File size must be less than 10MB";
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "image/png",
        "image/jpeg",
        "image/jpg",
      ];
      if (!allowedTypes.includes(data.file.type)) {
        errors.file =
          "File type not supported. Please upload PDF, DOC, DOCX, TXT, PNG, JPG, or JPEG files";
      }
    }
  } else {
    // For non-FILE categories, validate URL
    if (!data.url.trim()) {
      errors.url = "URL is required";
    } else if (data.url.length > DESTINATION_VALIDATION_RULES.url.maxLength) {
      errors.url = `URL must be less than ${DESTINATION_VALIDATION_RULES.url.maxLength} characters`;
    } else if (
      !DESTINATION_VALIDATION_RULES.url.pattern.test(
        formatDestinationUrl(data.url)
      )
    ) {
      errors.url =
        "Please enter a valid URL (must start with http:// or https://)";
    }
  }

  // Description validation
  if (
    data.description &&
    data.description.length > DESTINATION_VALIDATION_RULES.description.maxLength
  ) {
    errors.description = `Description must be less than ${DESTINATION_VALIDATION_RULES.description.maxLength} characters`;
  }

  // Tags validation
  if (data.tags.length > DESTINATION_VALIDATION_RULES.tags.maxCount) {
    errors.tags = `Maximum ${DESTINATION_VALIDATION_RULES.tags.maxCount} tags allowed`;
  } else if (
    data.tags.some(
      (tag) => tag.length > DESTINATION_VALIDATION_RULES.tags.maxLength
    )
  ) {
    errors.tags = `Each tag must be less than ${DESTINATION_VALIDATION_RULES.tags.maxLength} characters`;
  }

  return errors;
}

export function isDestinationFormValid(errors: DestinationFormErrors): boolean {
  return Object.keys(errors).length === 0;
}
